import { watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCategoryFiltersStore } from "~/stores/categoryFilters";

/**
 * Sprint F — 2-way-Binding zwischen `selected`-State im
 * categoryFilters-Store und Route-Query.
 *
 * Encoding: `?attr_material=eiche&attr_color=schwarz,weiss`
 * (flach, komma-getrennt, URI-sicher).
 */
const PREFIX = "attr_";

export function useAttributeFilterUrlState() {
    const route = useRoute();
    const router = useRouter();
    const store = useCategoryFiltersStore();

    /** Query → Store. Beim Page-Load + Route-Wechsel. */
    function readFromUrl() {
        const next: Record<string, string[]> = {};
        for (const [key, rawVal] of Object.entries(route.query)) {
            if (!key.startsWith(PREFIX)) continue;
            const attrKey = key.slice(PREFIX.length);
            if (!attrKey) continue;
            const vals = Array.isArray(rawVal) ? rawVal : [rawVal];
            const parsed = vals
                .flatMap((v) => (typeof v === "string" ? v.split(",") : []))
                .map((v) => v.trim())
                .filter(Boolean);
            if (parsed.length) next[attrKey] = parsed;
        }
        store.setAll(next);
    }

    /** Store → Query. Merged non-attr-Query-Params unverändert. */
    async function writeToUrl() {
        const current = { ...route.query };
        // Alle alten attr_* Einträge entfernen
        for (const k of Object.keys(current)) {
            if (k.startsWith(PREFIX)) delete current[k];
        }
        // Neue schreiben
        for (const [key, vals] of Object.entries(store.selected)) {
            if (vals.length) current[PREFIX + key] = vals.join(",");
        }
        await router.replace({ query: current });
    }

    // Store ↔ URL
    watch(() => route.query, readFromUrl, { immediate: true });
    watch(
        () => store.selected,
        () => {
            void writeToUrl();
        },
        { deep: true },
    );

    return { readFromUrl, writeToUrl };
}
